import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { personalMapApi } from '../../api/personalMapApi';
import SafeImage from '../../components/SafeImage';
import {
  Alert, Button, ConfirmDialog, EmptyState, ErrorState, LoadingState,
} from '../../components/ui';
import JourneyActivityMapSurface from '../journey-spatial/JourneyActivityMapSurface';
import {
  PERSONAL_MAP_CATEGORY_FILTERS,
  PERSONAL_MAP_SOURCE_FILTERS,
  appendPersonalMapItems,
  buildListParams,
  categoryLabel,
  normalizePersonalMapPage,
  personalMapActionErrorMessage,
  personalMapErrorMessage,
  sourceTypeLabel,
  toMapMarkers,
} from './personalMapModel';
import { canAddPersonalMapEntryToJourney } from './personalMapJourneyHandoff';
import PersonalMapAddToJourneySheet from './PersonalMapAddToJourneySheet';
import './personal-map.css';

function formatActivityAt(value, locale) {
  if (!value) return '';
  try {
    const tag = locale?.replace('_', '-') || undefined;
    return new Intl.DateTimeFormat(tag, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value));
  } catch {
    return String(value);
  }
}

function hasActiveFilters(sourceFilter, categoryFilter, includeHidden) {
  return sourceFilter !== PERSONAL_MAP_SOURCE_FILTERS.ALL
    || categoryFilter !== PERSONAL_MAP_CATEGORY_FILTERS.ALL
    || includeHidden;
}

function useWidePersonalMapLayout() {
  const [wide, setWide] = useState(false);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const query = window.matchMedia('(min-width: 768px)');
    const apply = () => setWide(query.matches);
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);
  return wide;
}

export default function PersonalMapPage() {
  const { locale, t } = useTravelTranslation();
  const titleId = useId();
  const [view, setView] = useState('map');
  const wideLayout = useWidePersonalMapLayout();
  const [sourceFilter, setSourceFilter] = useState(PERSONAL_MAP_SOURCE_FILTERS.ALL);
  const [categoryFilter, setCategoryFilter] = useState(PERSONAL_MAP_CATEGORY_FILTERS.ALL);
  const [includeHidden, setIncludeHidden] = useState(false);
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [paginationError, setPaginationError] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [hideTarget, setHideTarget] = useState(null);
  const [actionPending, setActionPending] = useState(false);
  const [actionError, setActionError] = useState('');
  const [addToJourneyOpen, setAddToJourneyOpen] = useState(false);
  const [addToJourneySeed, setAddToJourneySeed] = useState(null);
  const generation = useRef(0);
  const abortRef = useRef(null);
  const mountedRef = useRef(true);
  const previewCloseRef = useRef(null);
  const addToJourneyBtnRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    if (!selectedId || addToJourneyOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    function onKeyDown(event) {
      if (event.key === 'Escape' && !hideTarget && !addToJourneyOpen) {
        setSelectedId('');
        setActionError('');
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [selectedId, hideTarget, addToJourneyOpen]);

  const selected = useMemo(
    () => items.find((item) => item.locationId === selectedId) || null,
    [items, selectedId],
  );

  const markers = useMemo(() => toMapMarkers(items), [items]);
  const filtersActive = hasActiveFilters(sourceFilter, categoryFilter, includeHidden);

  const load = useCallback(async ({
    append = false,
    cursor = null,
    source = sourceFilter,
    category = categoryFilter,
    hidden = includeHidden,
  } = {}) => {
    const requestGeneration = ++generation.current;
    abortRef.current?.abort();
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    abortRef.current = controller;

    if (append) {
      setPaginationError('');
      setPhase('loading-more');
    } else {
      setError('');
      setPaginationError('');
      setPhase('loading');
    }

    try {
      const params = buildListParams({
        sourceFilter: source,
        categoryFilter: category,
        includeHidden: hidden,
        cursor,
      });
      const payload = await personalMapApi.list(params, controller ? { signal: controller.signal } : {});
      if (requestGeneration !== generation.current || !mountedRef.current) return;
      const page = normalizePersonalMapPage(payload, t);
      setItems((current) => (append ? appendPersonalMapItems(current, page.items) : page.items));
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
      setPhase('ready');
      if (!append) {
        setSelectedId((current) => {
          if (!current) return current;
          return page.items.some((item) => item.locationId === current) ? current : '';
        });
      }
    } catch (cause) {
      if (cause?.name === 'CanceledError' || cause?.name === 'AbortError' || cause?.code === 'ERR_CANCELED') {
        return;
      }
      if (requestGeneration !== generation.current || !mountedRef.current) return;
      if (append) {
        setPaginationError(personalMapErrorMessage(cause, t));
        setPhase('ready');
      } else {
        setError(personalMapErrorMessage(cause, t));
        setPhase('error');
      }
    }
  }, [sourceFilter, categoryFilter, includeHidden, t]);

  useEffect(() => {
    setItems([]);
    setNextCursor(null);
    setHasMore(false);
    setSelectedId('');
    load({
      source: sourceFilter,
      category: categoryFilter,
      hidden: includeHidden,
    });
  }, [sourceFilter, categoryFilter, includeHidden, load]);

  const clearFilters = () => {
    setSourceFilter(PERSONAL_MAP_SOURCE_FILTERS.ALL);
    setCategoryFilter(PERSONAL_MAP_CATEGORY_FILTERS.ALL);
    setIncludeHidden(false);
  };

  const openPreview = (locationId) => {
    setActionError('');
    setSelectedId(locationId);
  };

  const closePreview = () => {
    setSelectedId('');
    setActionError('');
    previewCloseRef.current?.focus?.();
  };

  const confirmHide = async () => {
    if (!hideTarget || actionPending) return;
    setActionPending(true);
    setActionError('');
    try {
      await personalMapApi.hide(hideTarget.locationId);
      if (!mountedRef.current) return;
      setHideTarget(null);
      if (includeHidden) {
        setItems((current) => current.map((item) => (
          item.locationId === hideTarget.locationId
            ? { ...item, hidden: true, status: 'HIDDEN' }
            : item
        )));
      } else {
        setItems((current) => current.filter((item) => item.locationId !== hideTarget.locationId));
        if (selectedId === hideTarget.locationId) setSelectedId('');
      }
    } catch (cause) {
      if (!mountedRef.current) return;
      setActionError(personalMapActionErrorMessage(cause, t) || t('travel.personalMap.hideFailed'));
    } finally {
      if (mountedRef.current) setActionPending(false);
    }
  };

  const restoreItem = async (entry) => {
    if (!entry || actionPending) return;
    setActionPending(true);
    setActionError('');
    try {
      await personalMapApi.restore(entry.locationId);
      if (!mountedRef.current) return;
      setItems((current) => current.map((item) => (
        item.locationId === entry.locationId
          ? { ...item, hidden: false, status: 'ACTIVE' }
          : item
      )));
    } catch (cause) {
      if (!mountedRef.current) return;
      setActionError(personalMapActionErrorMessage(cause, t) || t('travel.personalMap.restoreFailed'));
    } finally {
      if (mountedRef.current) setActionPending(false);
    }
  };

  return (
    <main className="personal-map" data-testid="personal-map-page" data-view={view} aria-labelledby={titleId}>
      <header className="personal-map__header">
        <Link className="personal-map__back" to="/me" data-testid="personal-map-back">
          {t('travel.personalMap.back')}
        </Link>
        <div className="personal-map__heading">
          <h1 id={titleId}>{t('travel.personalMap.title')}</h1>
          <p className="personal-map__privacy" data-testid="personal-map-privacy">
            {t('travel.personalMap.subtitle')}
          </p>
          <p className="personal-map__privacy-hint">{t('travel.personalMap.privacyHint')}</p>
        </div>
      </header>

      <div
        className="personal-map__toggle"
        role="group"
        aria-label={t('travel.personalMap.viewToggle')}
        data-testid="personal-map-view-toggle"
      >
        <button
          type="button"
          className={`personal-map__toggle-btn${view === 'map' ? ' is-active' : ''}`}
          aria-pressed={view === 'map'}
          onClick={() => setView('map')}
          data-testid="personal-map-view-map"
        >
          {t('travel.personalMap.viewMap')}
        </button>
        <button
          type="button"
          className={`personal-map__toggle-btn${view === 'list' ? ' is-active' : ''}`}
          aria-pressed={view === 'list'}
          onClick={() => setView('list')}
          data-testid="personal-map-view-list"
        >
          {t('travel.personalMap.viewList')}
        </button>
      </div>

      <section className="personal-map__filters" aria-label={t('travel.personalMap.filters')} data-testid="personal-map-filters">
        <label className="personal-map__filter">
          <span>{t('travel.personalMap.filterSource')}</span>
          <select
            value={sourceFilter}
            onChange={(event) => setSourceFilter(event.target.value)}
            data-testid="personal-map-filter-source"
          >
            <option value={PERSONAL_MAP_SOURCE_FILTERS.ALL}>{t('travel.personalMap.filterAll')}</option>
            <option value={PERSONAL_MAP_SOURCE_FILTERS.PLACE_POST}>{t('travel.personalMap.filterPlace')}</option>
            <option value={PERSONAL_MAP_SOURCE_FILTERS.FOOD_POST}>{t('travel.personalMap.filterFood')}</option>
            <option value={PERSONAL_MAP_SOURCE_FILTERS.CUSTOM_LOCATION}>{t('travel.personalMap.filterCustom')}</option>
          </select>
        </label>
        <label className="personal-map__filter">
          <span>{t('travel.personalMap.filterCategory')}</span>
          <select
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value)}
            data-testid="personal-map-filter-category"
          >
            <option value={PERSONAL_MAP_CATEGORY_FILTERS.ALL}>{t('travel.personalMap.filterAll')}</option>
            <option value={PERSONAL_MAP_CATEGORY_FILTERS.CHECK_IN}>{t('travel.personalMap.filterPlace')}</option>
            <option value={PERSONAL_MAP_CATEGORY_FILTERS.FOOD}>{t('travel.personalMap.filterFood')}</option>
            <option value={PERSONAL_MAP_CATEGORY_FILTERS.CUSTOM}>{t('travel.personalMap.filterCustom')}</option>
          </select>
        </label>
        <label className="personal-map__check">
          <input
            type="checkbox"
            checked={includeHidden}
            onChange={(event) => setIncludeHidden(event.target.checked)}
            data-testid="personal-map-include-hidden"
          />
          <span>{t('travel.personalMap.showHidden')}</span>
        </label>
        {filtersActive ? (
          <Button
            type="button"
            variant="secondary"
            onClick={clearFilters}
            data-testid="personal-map-clear-filters"
          >
            {t('travel.personalMap.clearFilters')}
          </Button>
        ) : null}
      </section>

      {phase === 'loading' ? (
        <LoadingState label={t('travel.personalMap.loading')} data-testid="personal-map-loading" />
      ) : null}

      {phase === 'error' ? (
        <ErrorState
          title={t('travel.personalMap.loadFailed')}
          description={error}
          retryable
          retryLabel={t('travel.personalMap.retry')}
          onRetry={() => load()}
          data-testid="personal-map-error"
        />
      ) : null}

      {phase === 'ready' && items.length === 0 ? (
        <EmptyState
          title={filtersActive
            ? t('travel.personalMap.emptyFilterTitle')
            : t('travel.personalMap.emptyAllTitle')}
          description={filtersActive
            ? t('travel.personalMap.emptyFilterHint')
            : t('travel.personalMap.emptyAllHint')}
          data-testid="personal-map-empty"
        />
      ) : null}

      {phase === 'ready' || phase === 'loading-more' ? (
        <>
          <div className="personal-map__workspace">
            {(wideLayout || view === 'map') ? (
            <div className="personal-map__map" data-testid="personal-map-map-panel">
              {markers.length === 0 && items.length > 0 ? (
                <Alert variant="info">{t('travel.personalMap.noCoordinates')}</Alert>
              ) : null}
              <JourneyActivityMapSurface
                markers={markers}
                selectedKey={selectedId}
                onSelectMarker={(key) => openPreview(key)}
                title=""
                emptyDescription={t('travel.personalMap.emptyAllHint')}
                disabledWarning={t('travel.personalMap.mapUnavailable')}
                showLegend={false}
                classPrefix="personal-map-surface"
                testId="personal-map-surface"
                emptyTestId="personal-map-surface-empty"
                googleTestId="personal-map-surface-google"
                loadingTestId="personal-map-surface-loading"
                planeAriaLabel={t('travel.personalMap.title')}
                mapAriaLabel={t('travel.personalMap.title')}
              />
              <p className="personal-map__bounds-note" data-testid="personal-map-bounds-note">
                {/* BOUNDS_INTERACTION_LIMITATION：既有 map 無 viewport callback；採 world query */}
              </p>
            </div>
            ) : null}
            {(wideLayout || view === 'list') ? (
            <ul className="personal-map__list" data-testid="personal-map-list">
              {items.map((item) => (
                <li key={item.locationId}>
                  <button
                    type="button"
                    className={`personal-map__card${selectedId === item.locationId ? ' is-selected' : ''}`}
                    onClick={() => openPreview(item.locationId)}
                    data-testid={`personal-map-card-${item.locationId}`}
                  >
                    {item.preview?.coverUrl ? (
                      <SafeImage className="personal-map__card-cover" src={item.preview.coverUrl} alt="" />
                    ) : (
                      <div className="personal-map__card-cover personal-map__card-cover--empty" aria-hidden="true" />
                    )}
                    <div className="personal-map__card-body">
                      <strong>{item.displayName}</strong>
                      <span>{categoryLabel(item.category, t)}</span>
                      {item.hidden ? <span className="personal-map__badge">{t('travel.personalMap.hiddenBadge')}</span> : null}
                      {item.lastActivityAt ? (
                        <span>
                          {t('travel.personalMap.lastActivity')}
                          {': '}
                          {formatActivityAt(item.lastActivityAt, locale)}
                        </span>
                      ) : null}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            ) : null}
          </div>

          {paginationError ? (
            <div className="personal-map__pagination-error" data-testid="personal-map-pagination-error">
              <Alert variant="error">{paginationError}</Alert>
              <Button
                type="button"
                onClick={() => load({ append: true, cursor: nextCursor })}
                data-testid="personal-map-pagination-retry"
              >
                {t('travel.personalMap.retry')}
              </Button>
            </div>
          ) : null}

          {hasMore && nextCursor ? (
            <div className="personal-map__more">
              <Button
                type="button"
                variant="secondary"
                loading={phase === 'loading-more'}
                onClick={() => load({ append: true, cursor: nextCursor })}
                data-testid="personal-map-load-more"
              >
                {phase === 'loading-more'
                  ? t('travel.personalMap.loadingMore')
                  : t('travel.personalMap.loadMore')}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}

      {selected ? (
        <div
          className="personal-map__preview-scrim"
          data-testid="personal-map-preview-scrim"
          onClick={closePreview}
          onKeyDown={(event) => {
            if (event.key === 'Escape') closePreview();
          }}
          role="presentation"
        >
          <aside
            className="personal-map__preview"
            role="dialog"
            aria-modal="true"
            aria-label={selected.displayName}
            data-testid="personal-map-preview"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="personal-map__preview-header">
              <h2>{selected.displayName}</h2>
              <button
                type="button"
                className="personal-map__preview-close"
                onClick={closePreview}
                ref={previewCloseRef}
                data-testid="personal-map-preview-close"
              >
                {t('travel.personalMap.previewClose')}
              </button>
            </div>
            <p>{categoryLabel(selected.category, t)}</p>
            {selected.hidden ? <p className="personal-map__badge">{t('travel.personalMap.hiddenBadge')}</p> : null}
            {selected.lastActivityAt ? (
              <p>
                {t('travel.personalMap.lastActivity')}
                {': '}
                {formatActivityAt(selected.lastActivityAt, locale)}
              </p>
            ) : null}
            {!selected.hasCoordinates ? <p>{t('travel.personalMap.noCoordinates')}</p> : null}
            <div className="personal-map__sources">
              <h3>{t('travel.personalMap.sources')}</h3>
              <ul>
                {selected.sources.map((source) => (
                  <li key={`${source.sourceType}:${source.sourceId}`}>
                    {sourceTypeLabel(source.sourceType, t)}
                    {source.occurredAt ? ` · ${formatActivityAt(source.occurredAt, locale)}` : ''}
                  </li>
                ))}
              </ul>
            </div>
            {actionError ? <Alert variant="error" data-testid="personal-map-action-error">{actionError}</Alert> : null}
            <div className="personal-map__preview-actions">
              {canAddPersonalMapEntryToJourney(selected, { actionPending }) ? (
                <Button
                  type="button"
                  ref={addToJourneyBtnRef}
                  disabled={actionPending || addToJourneyOpen}
                  onClick={() => {
                    if (actionPending || addToJourneyOpen) return;
                    setAddToJourneySeed({
                      locationId: selected.locationId,
                      displayName: selected.displayName,
                      category: selected.category,
                    });
                    setAddToJourneyOpen(true);
                  }}
                  data-testid="personal-map-add-to-journey"
                  aria-label={t('travel.personalMap.addToJourney')}
                >
                  {t('travel.personalMap.addToJourney')}
                </Button>
              ) : (
                <p
                  className="personal-map__add-unavailable"
                  data-testid="personal-map-add-to-journey-unavailable"
                >
                  {t('travel.personalMap.addToJourneyUnavailable')}
                </p>
              )}
              {selected.hidden ? (
                <Button
                  type="button"
                  loading={actionPending}
                  onClick={() => restoreItem(selected)}
                  data-testid="personal-map-restore"
                >
                  {actionPending ? t('travel.personalMap.restorePending') : t('travel.personalMap.restore')}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="secondary"
                  disabled={actionPending}
                  onClick={() => setHideTarget(selected)}
                  data-testid="personal-map-hide"
                >
                  {t('travel.personalMap.hide')}
                </Button>
              )}
            </div>
          </aside>
        </div>
      ) : null}

      <PersonalMapAddToJourneySheet
        open={addToJourneyOpen}
        seed={addToJourneySeed}
        onClose={() => {
          setAddToJourneyOpen(false);
          setAddToJourneySeed(null);
        }}
        onFocusRestore={() => {
          window.setTimeout(() => addToJourneyBtnRef.current?.focus?.(), 0);
        }}
      />

      <ConfirmDialog
        open={Boolean(hideTarget)}
        title={t('travel.personalMap.hideConfirmTitle')}
        description={t('travel.personalMap.hideConfirmBody')}
        confirmLabel={t('travel.personalMap.hideConfirmAction')}
        cancelLabel={t('travel.personalMap.hideCancel')}
        loading={actionPending}
        onConfirm={confirmHide}
        onCancel={() => {
          if (!actionPending) setHideTarget(null);
        }}
      />
    </main>
  );
}
